import { useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import WhatsAppFloat from "@/components/ui/whatsapp-float";
import SEO from "@/components/SEO";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Container from "@/components/ui/container";
import Breadcrumbs from "@/components/ui/breadcrumbs";
import FAQSection from "@/components/ui/faq-section";
import { AuroraHero } from "@/components/ui/aurora-hero";
import { 
  Monitor, 
  Code2, 
  Cpu, 
  Database,
  Cloud,
  Settings,
  Zap,
  CheckCircle,
  ArrowRight,
  Sparkles,
  Users,
  Clock,
  Shield,
  Smartphone,
  Globe,
  Server,
  GitBranch,
  Layers,
  TrendingUp
} from "lucide-react";
import RelatedLinks from "@/components/ui/related-links";

const ServicesTI = () => {
  const [activeTab, setActiveTab] = useState("outsourcing");

  const services = [
    {
      id: "outsourcing",
      icon: Monitor,
      title: "Outsourcing Fluig",
      description: "Equipe especializada dedicada para suporte e evolução da sua plataforma Fluig",
      color: "from-fs-purple-500 to-fs-cyan-500",
      details: {
        overview: "Nosso serviço de Outsourcing Fluig oferece uma equipe técnica dedicada e especializada para gerenciar, manter e evoluir sua plataforma Fluig. Garantimos operação contínua, suporte especializado e desenvolvimento de novas funcionalidades.",
        features: [
          "Equipe técnica dedicada exclusiva",
          "Suporte especializado 24x7",
          "Monitoramento proativo da plataforma",
          "Manutenção preventiva e corretiva",
          "Desenvolvimento de novas funcionalidades",
          "Consultoria técnica estratégica"
        ],
        benefits: [
          "Redução de até 70% nos custos de TI",
          "SLA garantido de 99.9% de disponibilidade",
          "Equipe sempre atualizada nas melhores práticas",
          "Foco total no core business da empresa"
        ],
        technologies: ["Fluig", "Java", "JavaScript", "ECM", "BPM", "REST API"],
        cases: [
          "Empresa de Logística - 300% aumento na eficiência dos processos",
          "Indústria Alimentícia - Redução de 80% no tempo de aprovações",
          "Grupo Empresarial - Unificação de 5 filiais em uma única plataforma"
        ]
      }
    },
    {
      id: "javascript",
      icon: Code2,
      title: "Desenvolvimento JavaScript/React",
      description: "Aplicações modernas e responsivas com as mais avançadas tecnologias frontend",
      color: "from-fs-cyan-500 to-fs-magenta-500",
      details: {
        overview: "Desenvolvimento de aplicações web modernas utilizando JavaScript/TypeScript, React, e as mais avançadas tecnologias frontend. Criamos interfaces responsivas, performáticas e com excelente experiência do usuário.",
        features: [
          "Aplicações React/TypeScript modernas",
          "Design responsivo mobile-first",
          "PWA (Progressive Web Apps)",
          "Integração com APIs REST/GraphQL",
          "Testes automatizados",
          "Deploy automatizado e CI/CD"
        ],
        benefits: [
          "Interfaces modernas e intuitivas",
          "Performance otimizada",
          "Compatibilidade cross-browser",
          "Facilidade de manutenção"
        ],
        technologies: ["React", "TypeScript", "Next.js", "Tailwind CSS", "Node.js", "GraphQL"],
        cases: [
          "Portal Corporativo - 500+ usuários simultâneos",
          "Dashboard Executivo - Visualização de dados em tempo real",
          "App Mobile Híbrido - Disponível iOS e Android"
        ]
      }
    },
    {
      id: "python",
      icon: Database,
      title: "Soluções Python",
      description: "Backend robusto e APIs escaláveis com Python e frameworks modernos",
      color: "from-fs-magenta-500 to-fs-purple-500",
      details: {
        overview: "Desenvolvimento de aplicações backend robustas e escaláveis utilizando Python e seus frameworks mais modernos. Especialistas em APIs REST, microserviços, automação e integração de sistemas.",
        features: [
          "APIs REST escaláveis com FastAPI/Django",
          "Microserviços e arquitetura distribuída",
          "Automação de processos e RPA",
          "Integração com bancos de dados",
          "Machine Learning e análise de dados",
          "Containerização com Docker"
        ],
        benefits: [
          "Código limpo e bem documentado",
          "Escalabilidade horizontal",
          "Performance otimizada",
          "Facilidade de integração"
        ],
        technologies: ["Python", "FastAPI", "Django", "PostgreSQL", "Redis", "Docker"],
        cases: [
          "API Gateway - 10M+ requisições/dia",
          "Sistema de Automação - 90% redução tempo manual",
          "Plataforma de Dados - Processamento de 1TB/dia"
        ]
      }
    },
    {
      id: "fluigdesk",
      icon: Smartphone,
      title: "FluigDESK",
      description: "Nossa versão exclusiva e otimizada da plataforma Fluig",
      color: "from-fs-purple-500 to-fs-cyan-500",
      details: {
        overview: "FluigDESK é nossa versão customizada e otimizada da plataforma Fluig, desenvolvida especificamente para atender as necessidades mais exigentes dos nossos clientes. Combina toda a robustez do Fluig com melhorias exclusivas da Future Station.",
        features: [
          "Interface redesenhada e otimizada",
          "Performance superior ao Fluig padrão",
          "Módulos customizados exclusivos",
          "Integrações nativas aprimoradas",
          "Dashboard executivo avançado",
          "Suporte dedicado Future Station"
        ],
        benefits: [
          "50% mais rápido que o Fluig tradicional",
          "Interface mais intuitiva e moderna",
          "Funcionalidades exclusivas",
          "Suporte especializado incluído"
        ],
        technologies: ["Fluig Core", "Java", "Angular", "PostgreSQL", "Redis", "Elasticsearch"],
        cases: [
          "Multinacional - Migração completa em 30 dias",
          "Governo Estadual - 10.000+ usuários ativos",
          "Holding Empresarial - 15 empresas conectadas"
        ]
      }
    }
  ];

  const activeService = services.find(s => s.id === activeTab);

  const stats = [
    { icon: Users, number: "50+", label: "Especialistas TI" },
    { icon: Clock, number: "24/7", label: "Suporte Técnico" },
    { icon: Shield, number: "99.9%", label: "SLA Garantido" },
    { icon: TrendingUp, number: "300%", label: "ROI Médio" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Serviços de TI Especializados em Fluig"
        description="Outsourcing Fluig especializado, desenvolvimento JavaScript/React, soluções Python e FluigDESK. SLA 99.9%, suporte 24/7 e ROI de 300%. Especialistas há 10+ anos."
        keywords={['outsourcing fluig', 'desenvolvimento javascript fluig', 'react fluig', 'python desenvolvimento', 'fluigdesk', 'serviços ti fluig', 'suporte técnico fluig', 'consultoria ti especializada']}
        type="website"
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section id="hero">
          <AuroraHero>
            <div className="text-center max-w-4xl mx-auto">
              {/* Badge */}
              <div className="inline-flex items-center space-x-2 bg-fs-purple-500/10 backdrop-blur-sm border border-fs-purple-500/30 rounded-full px-6 py-3 mb-8">
                <Monitor className="w-5 h-5 text-fs-cyan-400" />
                <span className="text-foreground dark:text-white/90 font-medium">
                  Serviços de TI
                </span>
              </div>

              {/* Title */}
              <h1 className="text-5xl md:text-6xl font-bold font-poppins text-foreground dark:text-white mb-6">
                Tecnologia da
                <span className="block fs-gradient-text mt-2">
                  Informação
                </span>
              </h1>

              {/* Description */}
              <p className="text-xl text-muted-foreground dark:text-white/70 leading-relaxed mb-12 max-w-3xl mx-auto">
                Soluções completas de TI com foco em Fluig: desde outsourcing especializado
                até desenvolvimento customizado com tecnologias modernas.
              </p>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
                {stats.map((stat, index) => (
                  <div key={stat.label} className="text-center">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-fs-purple-500 to-fs-cyan-500 mb-3">
                      <stat.icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-2xl md:text-3xl font-bold fs-gradient-text mb-1">
                      {stat.number}
                    </div>
                    <div className="text-sm text-muted-foreground dark:text-white/70">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
                <Button variant="fs-primary" size="lg" className="group">
                  Solicitar Consultoria
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button variant="fs-outline" size="lg">
                  Ver Cases de Sucesso
                </Button>
              </div>
            </div>
          </AuroraHero>
        </section>

        {/* Services Navigation */}
        <section className="py-12 bg-fs-section border-b border-border">
          <Container>
            <div className="flex flex-wrap justify-center gap-4">
              {services.map((service) => {
                const IconComponent = service.icon;
                return (
                  <button
                    key={service.id}
                    onClick={() => setActiveTab(service.id)}
                    className={`flex items-center space-x-3 px-6 py-3 rounded-xl transition-all duration-300 ${
                      activeTab === service.id
                        ? 'bg-gradient-to-r from-fs-purple-500 to-fs-cyan-500 text-white scale-105'
                        : 'bg-card dark:bg-gray-900/50 text-foreground dark:text-white/70 hover:text-foreground dark:hover:text-white hover:bg-muted dark:hover:bg-gray-900/70'
                    }`}
                  >
                    <IconComponent className="w-5 h-5" />
                    <span className="font-medium">{service.title}</span>
                  </button>
                );
              })}
            </div>
          </Container>
        </section>

        {/* Active Service Details */}
        {activeService && (
          <section className="py-20 bg-fs-section">
            <Container>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                {/* Left Column - Service Info */}
                <div>
                  <div className="flex items-center space-x-4 mb-6">
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${activeService.color} p-4`}>
                      <activeService.icon className="w-full h-full text-white" />
                    </div>
                    <div>
                      <h2 className="text-3xl font-bold font-poppins text-foreground dark:text-white">
                        {activeService.title}
                      </h2>
                      <p className="text-fs-cyan-400">{activeService.description}</p>
                    </div>
                  </div>

                  <div className="space-y-8">
                    {/* Overview */}
                    <div>
                      <h3 className="text-xl font-semibold text-foreground dark:text-white mb-4">Visão Geral</h3>
                      <p className="text-muted-foreground dark:text-white/70 leading-relaxed">
                        {activeService.details.overview}
                      </p>
                    </div>

                    {/* Features */}
                    <div>
                      <h3 className="text-xl font-semibold text-foreground dark:text-white mb-4">Principais Funcionalidades</h3>
                      <ul className="space-y-3">
                        {activeService.details.features.map((feature, index) => (
                          <li key={index} className="flex items-start space-x-3">
                            <CheckCircle className="w-5 h-5 text-fs-cyan-400 flex-shrink-0 mt-0.5" />
                            <span className="text-muted-foreground dark:text-white/70">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Technologies */}
                    <div>
                      <h3 className="text-xl font-semibold text-foreground dark:text-white mb-4">Tecnologias Utilizadas</h3>
                      <div className="flex flex-wrap gap-2">
                        {activeService.details.technologies.map((tech, index) => (
                          <Badge 
                            key={index} 
                            variant="secondary" 
                            className="bg-fs-purple-500/20 text-fs-purple-200 border-fs-purple-500/30"
                          >
                            {tech}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column - Benefits and Cases */}
                <div className="space-y-8">
                  {/* Benefits Card */}
                  <Card className="bg-card dark:bg-gray-900/50 backdrop-blur-sm border border-fs-purple-500/20">
                    <CardHeader>
                      <CardTitle className="flex items-center space-x-3 text-foreground dark:text-white">
                        <Zap className="w-6 h-6 text-fs-cyan-400" />
                        <span>Principais Benefícios</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-3">
                        {activeService.details.benefits.map((benefit, index) => (
                          <li key={index} className="flex items-start space-x-3">
                            <div className="w-2 h-2 bg-fs-magenta-400 rounded-full flex-shrink-0 mt-2" />
                            <span className="text-muted-foreground dark:text-white/80">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>

                  {/* Cases Card */}
                  <Card className="bg-card dark:bg-gray-900/50 backdrop-blur-sm border border-fs-purple-500/20">
                    <CardHeader>
                      <CardTitle className="flex items-center space-x-3 text-foreground dark:text-white">
                        <TrendingUp className="w-6 h-6 text-fs-magenta-400" />
                        <span>Cases de Sucesso</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {activeService.details.cases.map((case_study, index) => (
                          <div key={index} className="p-4 bg-fs-purple-500/10 rounded-lg border border-fs-purple-500/20">
                            <p className="text-muted-foreground dark:text-white/70 text-sm leading-relaxed">
                              {case_study}
                            </p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* CTA Card */}
                  <Card className="bg-gradient-to-br from-fs-purple-500/20 to-fs-cyan-500/20 border border-fs-purple-500/30">
                    <CardContent className="p-6 text-center">
                      <h3 className="text-xl font-bold text-foreground dark:text-white mb-3">
                        Interessado nesta solução?
                      </h3>
                      <p className="text-muted-foreground dark:text-white/70 mb-6">
                        Fale com nossos especialistas e descubra como podemos
                        otimizar sua infraestrutura de TI.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-3">
                        <Button variant="fs-primary" className="flex-1">
                          <Users className="w-4 h-4 mr-2" />
                          Falar com Especialista
                        </Button>
                        <Button variant="fs-outline" className="flex-1">
                          Solicitar Proposta
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </Container>
          </section>
        )}

        {/* Additional Services Section */}
        <section className="py-20 bg-gradient-to-b from-fs-section to-fs-section/50">
          <Container>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold font-poppins text-foreground dark:text-white mb-4">
                Outros Serviços
                <span className="block fs-gradient-text mt-2">
                  Especializados
                </span>
              </h2>
              <p className="text-muted-foreground dark:text-white/70 max-w-2xl mx-auto">
                Explore nossas outras áreas de expertise para uma solução completa.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  title: "Recursos Humanos",
                  description: "Automação de processos de RH com admissão digital e gestão de férias.",
                  icon: Users,
                  href: "/servicos/rh",
                  color: "from-fs-cyan-500 to-fs-magenta-500"
                },
                {
                  title: "Gestão da Qualidade",
                  description: "Controle de não conformidades e gestão completa de documentos.",
                  icon: Shield,
                  href: "/servicos/qualidade",
                  color: "from-fs-magenta-500 to-fs-purple-500"
                },
                {
                  title: "Processos Financeiros",
                  description: "Automação de compras, fornecedores e reembolsos de despesas.",
                  icon: TrendingUp,
                  href: "/servicos/financeiro",
                  color: "from-fs-purple-500 to-fs-cyan-500"
                }
              ].map((service, index) => (
                <Link key={service.title} to={service.href}>
                  <Card className="group bg-card dark:bg-gray-900/50 backdrop-blur-sm border border-fs-purple-500/20 hover:-translate-y-2 transition-all duration-500 hover:shadow-fs-hover hover:border-fs-purple-500/50 h-full">
                    <CardContent className="p-6">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${service.color} p-3 mb-4 group-hover:scale-110 transition-transform duration-300`}>
                        <service.icon className="w-full h-full text-white" />
                      </div>
                      <h3 className="text-lg font-bold text-foreground dark:text-white mb-3 group-hover:fs-gradient-text transition-all duration-300">
                        {service.title}
                      </h3>
                      <p className="text-muted-foreground dark:text-white/70 text-sm leading-relaxed mb-4">
                        {service.description}
                      </p>
                      <Button variant="fs-outline" size="sm" className="w-full group-hover:bg-fs-purple-500/20">
                        Saiba Mais
                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </Container>
        </section>

        {/* Related Links */}
        <section className="py-20 bg-gradient-to-b from-fs-section to-fs-section/50">
          <div className="max-w-6xl mx-auto px-6">
            <RelatedLinks 
              title="Explore Outros Serviços"
              links={[
                {
                  title: "Serviços de Recursos Humanos",
                  description: "Automatize processos de RH com admissão digital, gestão de férias e workflows de aprovação integrados.",
                  href: "/servicos/rh",
                  category: "Serviços"
                },
                {
                  title: "Consultoria Fluig Especializada",
                  description: "Maximize o ROI da sua plataforma Fluig com nossa consultoria estratégica e 50.000+ horas de experiência.",
                  href: "/consultoria-fluig",
                  category: "Consultoria"
                },
                {
                  title: "Automação de Processos",
                  description: "Transforme processos manuais em workflows automatizados com nossa expertise em BPM e tecnologia.",
                  href: "/automacao-processos",
                  category: "Automação"
                },
                {
                  title: "Catálogo FluigApps",
                  description: "Acelere sua implementação com nossos 16 FluigApps prontos para diversos processos empresariais.",
                  href: "/flows",
                  category: "Soluções"
                }
              ]}
            />
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20">
          <Container>
            <FAQSection 
              title="Perguntas Frequentes - TI"
              description="Esclareça suas dúvidas sobre nossos serviços de Tecnologia da Informação"
              faqs={[
                {
                  question: "O que inclui o serviço de Outsourcing Fluig?",
                  answer: "Nosso Outsourcing Fluig inclui equipe técnica dedicada, suporte 24x7, monitoramento proativo, manutenção preventiva e corretiva, desenvolvimento de novas funcionalidades, gestão de atualizações e backup/disaster recovery. Garantimos SLA de 99.9% de disponibilidade."
                },
                {
                  question: "Quanto tempo demora para implementar uma solução customizada?",
                  answer: "O prazo varia conforme a complexidade do projeto. Soluções simples podem ser entregues em 2-4 semanas, enquanto projetos complexos podem levar 2-3 meses. Utilizamos metodologia ágil com entregas incrementais a cada sprint de 2 semanas."
                },
                {
                  question: "Vocês trabalham com desenvolvimento em quais tecnologias?",
                  answer: "Somos especialistas em JavaScript/TypeScript para Fluig, React.js para interfaces modernas, Python para integrações e automações, Node.js para APIs, e FluigDESK para aplicações desktop. Também trabalhamos com bancos de dados SQL Server, Oracle e PostgreSQL."
                },
                {
                  question: "Como funciona o suporte técnico 24x7?",
                  answer: "Oferecemos suporte técnico contínuo através de múltiplos canais: WhatsApp para urgências, sistema de tickets para controle, telefone dedicado e acesso remoto quando necessário. Nossa equipe é composta por especialistas certificados TOTVS Fluig."
                },
                {
                  question: "Qual o investimento para contratar os serviços de TI?",
                  answer: "O investimento varia conforme o escopo e complexidade. Oferecemos modelos flexíveis: mensalidade fixa para outsourcing, projeto fechado para desenvolvimentos específicos, ou hora técnica para demandas pontuais. Entre em contato para um orçamento personalizado."
                },
                {
                  question: "Vocês oferecem migração de outras plataformas para Fluig?",
                  answer: "Sim, realizamos migrações completas de outras plataformas BPM/ECM para Fluig. Isso inclui análise da plataforma atual, mapeamento de processos, migração de dados, recriação de workflows e treinamento da equipe. Garantimos zero perda de dados."
                }
              ]}
            />
          </Container>
        </section>
      </main>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
};

export default ServicesTI;